import React, { useState, useRef } from 'react';
import { devLog, devWarn, devError } from '../../../../utils/devLogger';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import GenericListUI, { FetchParams, FetchResult } from '../../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../../components/CustomList/GenericDataTable';
import { AuctionRegistrationWithBidUnitPopup, AuctionWithBidUnitData } from './AuctionRegistrationWithBidUnitPopup';
import LotOrderManagementPopup from './LotOrderManagementPopup';
import Modal from '../../../../components/Modal';
import CmsPopupBtn from '../../../../components/CmsPopupBtn';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';
import dayjs from 'dayjs';
import { getAuctionList, createAuction, updateAuction, deleteAuction } from '../../../../lib/api/admin/adminApi';
import { AuctionData, CreateAuctionData, UpdateAuctionData } from '../../../../lib/api/admin/adminApi.types';

// 날짜를 분 단위까지만 사용하도록 초/밀리초를 0으로 맞춘 ISO 문자열로 변환하는 유틸
const toMinutePrecisionISOString = (date?: Date | null): string | undefined => {
  if (!date) return undefined;
  const normalized = new Date(date);
  normalized.setSeconds(0, 0);
  return normalized.toISOString();
};

const AuctionManagementPage: React.FC = () => {
  devLog('AuctionManagementPage 렌더링 시작');
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingAuction, setEditingAuction] = useState<AuctionData | null>(null);
  const [isLotOrderPopupOpen, setIsLotOrderPopupOpen] = useState(false);
  const [selectedAuctionForLot, setSelectedAuctionForLot] = useState<AuctionData | null>(null);
  
  // LOT 추가 등록 확인 모달 상태
  const [isLotAddConfirmModalOpen, setIsLotAddConfirmModalOpen] = useState(false);
  const [newlyCreatedAuction, setNewlyCreatedAuction] = useState<AuctionData | null>(null);

  // 입찰단위 데이터를 API 형식으로 변환하는 함수
  const convertBidUnitToAPIFormat = (bidUnitRanges: any[]) => {
    return bidUnitRanges.map((range, index) => {
      const isLastRange = index === bidUnitRanges.length - 1;
      const biddingRate: any = {
        minBidAmount: Number(range.minAmount),
        rate: Number(range.unitAmount),
        delete: range.delete ?? false // delete 속성 추가
      };
      
      // 마지막 구간이 아닌 경우에만 maxBidAmount 포함
      if (!isLastRange) {
        biddingRate.maxBidAmount = Number(range.maxAmount);
      }
      
      return biddingRate;
    });
  };

  // 사전입찰이 시작되었는지 확인하는 함수
  const isPreBidStarted = (preBidStartAt: string) => {
    if (!preBidStartAt) return false;
    const preBidDate = new Date(preBidStartAt);
    const currentDate = new Date();
    return preBidDate <= currentDate;
  };

  // Lot 수 클릭 핸들러
  const handleLotCountClick = (auctionData: AuctionData) => {
    devLog('Lot 수 클릭:', auctionData);
    devLog('사전입찰 시작 여부:', isPreBidStarted(auctionData.preBidStartAt));
    
    setSelectedAuctionForLot(auctionData);
    setIsLotOrderPopupOpen(true);
  };

  // 컬럼 정의
  const columns: ColumnDefinition<AuctionData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '등록일시',
      accessor: 'createAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '사전입찰 시작일시',
      accessor: 'preBidStartAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '사전입찰 종료일시',
      accessor: 'preBidEndAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '경매 시작일시',
      accessor: 'auctionStartAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '경매 종료일시',
      accessor: 'auctionEndAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '결제마감일',
      accessor: 'paymentDueAt',
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD');
      },
    },
    {
      header: '경매ID',
      accessor: 'code',
      formatter: (value) => value || '',
    },
    {
      header: '경매사',
      accessor: 'auctioneer.name', // nested 객체 접근
      formatter: (value, item) => {
        // nested 객체 접근을 위해 item에서 직접 추출
        return item?.auctioneer?.name || '';
      },
    },
    {
      header: '경매명',
      accessor: 'name', // API 응답에서는 name 필드
      formatter: (value) => value || '',
    },
    {
      header: '경매 상세',
      accessor: 'description', // API 응답에서는 description 필드
      formatter: (value) => {
        if (!value) return '-';
        // 긴 설명은 50자로 제한하고 말줄임표 추가
        return value.length > 50 ? `${value.substring(0, 50)}...` : value;
      },
    },
    {
      header: '경매 수수료',
      accessor: 'auctionFeeRate', // API 응답에서는 auctionFeeRate 필드
      formatter: (value) => {
        if (value === undefined || value === null) return '-';
        return `${value}%`;
      },
    },
    {
      header: 'Lot 수',
      accessor: 'lots', // lots 배열의 길이를 사용
      formatter: (value, item) => {
        const lotCount = item?.lots?.length || 0;
        return (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLotCountClick(item as AuctionData);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              minHeight: '18px',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                fontWeight: '500',
              }}
            >
              {lotCount}
            </span>
          </div>
        );
      },
      excelFormatter: (value, item) => {
        return item?.lots?.length || 0;
      },
    },
  ];

  // 실제 API 함수
  const fetchAuctionData = async (params: FetchParams): Promise<FetchResult<AuctionData>> => {
    devLog('fetchAuctionData 호출됨:', params);
    
    try {
      const response = await getAuctionList({
        keyword: params.keyword,
        fromDate: params.fromDate,
        toDate: params.toDate,
      });

      devLog('서버 응답:', response);

      // 서버 응답 구조: { statusCode, message, data: [], metadata: { allCnt, totalCnt } }
      let data: AuctionData[] = [];
      let totalCount = 0;
      let allCount = 0;

      if (response?.data) {
        // 서버 응답이 { data: [], metadata: {} } 형태인 경우
        if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
          // metadata가 있는 경우
          if (response.data.metadata) {
            totalCount = response.data.metadata.totalCnt || data.length;
            allCount = response.data.metadata.allCnt || data.length;
          } else {
            totalCount = data.length;
            allCount = data.length;
          }
        }
        // 예전 형태로 직접 배열인 경우 (호환성)
        else if (Array.isArray(response.data)) {
          data = response.data;
          totalCount = data.length;
          allCount = data.length;
        }
        // 다른 형태인 경우 빈 배열로 초기화
        else {
          devWarn('예상치 못한 서버 응답 구조:', response.data);
          data = [];
          totalCount = 0;
          allCount = 0;
        }
      }
      
      const result: FetchResult<AuctionData> = {
        data: data,
        totalItems: totalCount,
        allItems: allCount,
      };

      devLog('가공된 결과:', result);
      return result;
    } catch (error) {
      toast.error('경매 리스트를 불러오는 중 오류가 발생했습니다.');
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // API의 biddingRanges를 팝업에서 사용하는 bidUnitRanges 형식으로 변환하는 함수
  const convertBiddingRangesToBidUnitRanges = (biddingRanges: any[]) => {
    if (!biddingRanges || biddingRanges.length === 0) {
      // 기본값 반환
      return [
        {
          index: 1,
          minAmount: "1",
          maxAmount: "100000",
          unitAmount: "1000",
        },
        {
          index: 2,
          minAmount: "100001",
          maxAmount: "",
          unitAmount: "10000",
        },
      ];
    }

    return biddingRanges.map((range, index) => ({
      index: index + 1,
      minAmount: range.minBidAmount?.toString() || '',
      maxAmount: range.maxBidAmount?.toString() || '',
      unitAmount: range.rate?.toString() || '',
    }));
  };

  // 행 클릭 핸들러
  const handleRowClick = (item: AuctionData, index: number) => {
    devLog('경매 선택:', item);
    devLog('전달할 initialData:', {
      auctioneerId: item.auctioneer?.index?.toString() || '',
      auctioneerName: item.auctioneer?.name || '',
      auctionName: item.name || '',
      auctionDetail: item.description || '',
      streamingUrl: item.url || '',
      commissionRate: item.auctionFeeRate || 0,
      platformCommissionRate: item.platformFeeRate || 0,
      preBidStartDate: item.preBidStartAt ? new Date(item.preBidStartAt) : null,
      auctionStartDate: item.auctionStartAt ? new Date(item.auctionStartAt) : null,
      auctionEndDate: item.auctionEndAt ? new Date(item.auctionEndAt) : null,
      paymentDeadline: item.paymentDueAt ? new Date(item.paymentDueAt) : null,
    });
    setEditingAuction(item); // 수정할 경매 데이터 설정
    setIsPopupOpen(true); // 팝업 열기
  };

  // 경매 등록 핸들러
  const handleAddAuction = () => {
    setEditingAuction(null);
    setIsPopupOpen(true);
  };

  // 팝업 닫기 핸들러
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setEditingAuction(null);
  };

  // 경매 저장 핸들러
  const handleSaveAuction = async (formData: AuctionWithBidUnitData) => {
    try {
      // 입찰단위 데이터는 popup에서 이미 변환된 biddingRates를 사용
      const biddingRates = formData.biddingRates || convertBidUnitToAPIFormat(formData.bidUnitRanges);
      devLog('변환된 입찰단위 데이터:', biddingRates);
      
      if (editingAuction) {
        // 수정 모드
        const preBidAlreadyStarted = isPreBidStarted(editingAuction.preBidStartAt);

        // 사전입찰이 이미 시작된 경우에는 서버에 저장되어 있는 원본 사전입찰 시작일시를 그대로 사용하고,
        // 아직 시작 전이라면 폼에서 입력한 값을 사용하되, 분 단위까지만 전송한다.
        const preBidStartAtToSend = preBidAlreadyStarted
          ? editingAuction.preBidStartAt
          : toMinutePrecisionISOString(formData.preBidStartDate);

        const updateData: UpdateAuctionData = {
          name: formData.auctionName,
          description: formData.auctionDetail,
          url: formData.streamingUrl,
          preBidStartAt: preBidStartAtToSend,
          auctionStartAt: formData.auctionStartDate?.toISOString(),
          auctionEndAt: formData.auctionEndDate?.toISOString(),
          paymentDueAt: formData.paymentDeadline ? 
            `${formData.paymentDeadline.getFullYear()}-${String(formData.paymentDeadline.getMonth() + 1).padStart(2, '0')}-${String(formData.paymentDeadline.getDate()).padStart(2, '0')}` : 
            undefined,
          auctionFeeRate: formData.commissionRate,
          platformFeeRate: formData.platformCommissionRate,
          auctioneerIndex: parseInt(formData.auctioneerId, 10),
          biddingRates: biddingRates,
        };

        devLog('경매 수정:', editingAuction.index, updateData);
        const response = await updateAuction(editingAuction.index, updateData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success('경매가 수정되었습니다.');
          handleClosePopup();
          setRefreshTrigger(prev => prev + 1);
        } else {
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '수정 처리 중 오류가 발생했습니다.';
          toast.error(`경매 수정에 실패했습니다. (${errorMessage})`);
        }
      } else {
        // 생성 모드
        const createData: CreateAuctionData = {
          name: formData.auctionName,
          description: formData.auctionDetail,
          url: formData.streamingUrl,
          preBidStartAt: toMinutePrecisionISOString(formData.preBidStartDate) || '',
          auctionStartAt: formData.auctionStartDate?.toISOString() || '',
          auctionEndAt: formData.auctionEndDate?.toISOString() || '',
          paymentDueAt: formData.paymentDeadline ? 
            `${formData.paymentDeadline.getFullYear()}-${String(formData.paymentDeadline.getMonth() + 1).padStart(2, '0')}-${String(formData.paymentDeadline.getDate()).padStart(2, '0')}` : 
            '',
          auctionFeeRate: formData.commissionRate,
          platformFeeRate: formData.platformCommissionRate,
          auctioneerIndex: parseInt(formData.auctioneerId, 10),
          biddingRates: biddingRates,
        };

        devLog('경매 등록:', createData);
        const response = await createAuction(createData);
        
        // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
        if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
          toast.success('경매가 등록되었습니다.');
          handleClosePopup();
          setRefreshTrigger(prev => prev + 1);
          
          // 새로 등록된 경매 정보 저장 (API 응답에서 데이터를 가져오거나 기본값으로 설정)
          const createdAuction: AuctionData = {
            index: response.data.data?.index || response.data.data?.id || Date.now(), // index가 없으면 임시 ID 사용
            no: response.data.data?.no || 0,
            id: response.data.data?.id || response.data.data?.index || Date.now(),
            name: formData.auctionName,
            description: formData.auctionDetail,
            url: formData.streamingUrl,
            preBidStartAt: toMinutePrecisionISOString(formData.preBidStartDate) || '',
            preBidEndAt: '',
            auctionStartAt: formData.auctionStartDate?.toISOString() || '',
            auctionEndAt: formData.auctionEndDate?.toISOString() || '',
            paymentDueAt: formData.paymentDeadline ? 
              `${formData.paymentDeadline.getFullYear()}-${String(formData.paymentDeadline.getMonth() + 1).padStart(2, '0')}-${String(formData.paymentDeadline.getDate()).padStart(2, '0')}` : 
              '',
            auctionFeeRate: formData.commissionRate,
            platformFeeRate: formData.platformCommissionRate,
            auctioneer: {
              index: parseInt(formData.auctioneerId, 10),
              profile_pic: '',
              name: formData.auctioneerName || '',
              description: '',
              term: '',
              create_by: '',
              update_by: null,
              create_at: new Date().toISOString(),
              update_at: null
            },
            lots: [],
            preBids: [],
            biddingRanges: [],
            createAt: new Date().toISOString(),
            updateAt: new Date().toISOString()
          };
          
          devLog('=== 새로 생성된 경매 정보 ===');
          devLog('createdAuction:', createdAuction);
          devLog('API 응답 데이터:', response.data.data);
          
          setNewlyCreatedAuction(createdAuction);
          setIsLotAddConfirmModalOpen(true);
        } else {
          const errorMessage = response?.data?.error?.customMessage || 
                              response?.data?.message || 
                              '등록 처리 중 오류가 발생했습니다.';
          toast.error(`경매 등록에 실패했습니다. (${errorMessage})`);
        }
      }
    } catch (error: any) {
      devError('경매 저장 에러:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      if (editingAuction) {
        toast.error(`경매 수정에 실패했습니다. (${errorMessage})`);
      } else {
        toast.error(`경매 등록에 실패했습니다. (${errorMessage})`);
      }
    }
  };

  // 경매 삭제 핸들러
  const handleDeleteAuction = async () => {
    if (!editingAuction?.index) {
      toast.error('경매 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const response = await deleteAuction(editingAuction.index);
      
      // API 응답 구조 확인: statusCode가 200이고 message가 "success"일 때만 성공
      if (response?.data?.statusCode === 200 && response?.data?.message === "success") {
        toast.success('경매가 삭제되었습니다.');
        handleClosePopup();
        setRefreshTrigger(prev => prev + 1);
      } else {
        const errorMessage = response?.data?.error?.customMessage || 
                            response?.data?.message || 
                            '삭제 처리 중 오류가 발생했습니다.';
        toast.error(`경매 삭제에 실패했습니다. (${errorMessage})`);
      }
    } catch (error: any) {
      devError('경매 삭제 에러:', error);
      
      // callApi에서 던진 에러 처리
      const errorMessage = error?.data?.error?.customMessage || 
                          error?.data?.message ||
                          error?.response?.data?.error?.customMessage || 
                          error?.response?.data?.message || 
                          error?.message || 
                          '네트워크 오류가 발생했습니다.';
      
      toast.error(`경매 삭제에 실패했습니다. (${errorMessage})`);
    }
  };

  // LOT 추가 등록 확인 모달 핸들러들
  const handleLotAddConfirm = () => {
    if (newlyCreatedAuction) {
      devLog('LOT 등록하기 버튼 클릭 - 새로 생성된 경매:', newlyCreatedAuction);
      setSelectedAuctionForLot(newlyCreatedAuction);
      
      // 경매 목록을 먼저 새로고침하고 약간의 지연 후 팝업 열기
      setRefreshTrigger(prev => prev + 1);
      
      setTimeout(() => {
        setIsLotOrderPopupOpen(true);
      }, 500); // 500ms 지연으로 API 호출이 완료될 시간을 제공
    }
    setIsLotAddConfirmModalOpen(false);
    setNewlyCreatedAuction(null);
  };

  const handleLotAddCancel = () => {
    setIsLotAddConfirmModalOpen(false);
    setNewlyCreatedAuction(null);
  };

  return (
    <>
      <GenericListUI<AuctionData>
        ref={listRef}
        title="경매 관리"
        columns={columns}
        fetchData={fetchAuctionData}
        excelFileName="AuctionList"
        keyExtractor={(item, index) => {
          return item.id ? item.id.toString() : `auction-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="경매ID, 경매사, 경매명, 경매 상세로 검색"
        enableDateFilter={true}
        dateRangeOptions={['3개월', '6개월', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
        addButton={{
          label: "경매 등록",
          onClick: handleAddAuction
        }}
      />
      
      <AuctionRegistrationWithBidUnitPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onSubmit={handleSaveAuction}
        onDelete={editingAuction ? handleDeleteAuction : undefined}
        mode={editingAuction ? 'edit' : 'create'}
        initialData={editingAuction ? {
          auctioneerId: editingAuction.auctioneer?.index?.toString() || '', // auctioneer.index를 auctioneerId로 매핑
          auctioneerName: editingAuction.auctioneer?.name || '',
          auctionName: editingAuction.name || '',
          auctionDetail: editingAuction.description || '',
          streamingUrl: editingAuction.url || '',
          commissionRate: editingAuction.auctionFeeRate || 0,
          platformCommissionRate: editingAuction.platformFeeRate || 0,
          preBidStartDate: editingAuction.preBidStartAt ? new Date(editingAuction.preBidStartAt) : null,
          auctionStartDate: editingAuction.auctionStartAt ? new Date(editingAuction.auctionStartAt) : null,
          auctionEndDate: editingAuction.auctionEndAt ? new Date(editingAuction.auctionEndAt) : null,
          paymentDeadline: editingAuction.paymentDueAt ? new Date(editingAuction.paymentDueAt) : null,
          hasPreBids: editingAuction.preBids && editingAuction.preBids.length > 0, // preBids 배열이 있고 길이가 0보다 크면 true
        } : undefined}
        initialBidUnitRanges={editingAuction ? convertBiddingRangesToBidUnitRanges(editingAuction.biddingRanges) : undefined}
      />

      <LotOrderManagementPopup
        isOpen={isLotOrderPopupOpen}
        onClose={() => {
          setIsLotOrderPopupOpen(false);
          setSelectedAuctionForLot(null);
        }}
        onSuccess={() => {
          // 로트 순서 저장 성공 시 테이블 새로고침
          setRefreshTrigger(prev => prev + 1);
        }}
        auctioneerData={selectedAuctionForLot ? {
          index: selectedAuctionForLot.auctioneer.index,
          name: selectedAuctionForLot.auctioneer.name,
        } : undefined}
        selectedAuctionData={selectedAuctionForLot ? {
          index: selectedAuctionForLot.index,
          name: selectedAuctionForLot.name,
          preBidStartAt: selectedAuctionForLot.preBidStartAt, // 사전입찰 시작 시간 추가
        } : undefined}
      />

      {/* LOT 추가 등록 확인 모달 */}
      <Modal
        isOpen={isLotAddConfirmModalOpen}
        onClose={handleLotAddCancel}
        header="LOT 등록 안내"
        body={`경매가 성공적으로 등록되었습니다.\n등록한 경매에 LOT을 추가하시겠습니까?`}
        width="400px"
        bgColor="#ffffff"
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleLotAddCancel}
            >
              나중에 등록
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleLotAddConfirm}
            >
              LOT 등록하기
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />
    </>
  );
};

// Styled Components (AdminFormPopup과 동일한 스타일)
const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  
  & > * {
    flex: 1;
  }
`;

export default AuctionManagementPage;
